import * as React from 'react';
import type {SelectionModeProps, SelectionModeMeta} from './_types';

const checkHasMaxSelections = ({
  maxSelections,
  value,
}: {
  maxSelections: SelectionModeProps['maxSelections'];
  value: SelectionModeProps['value'];
}) => {
  if (maxSelections && Array.isArray(value)) {
    return value?.length >= maxSelections;
  }

  return false;
};

/**
 * A hook used interally to facilitate single and multiple item selection.
 *
 * ## Usage
 * ```javascript
 * import {RowItemBody, RowItemCheckbox, useSelection} from '@packages/systems/spring';
 *
 * const Example = ({value}) => {
 *  const {toggleSelectedValue, getIsSelected} = useSelection({onChange, selectionMode, value});
 *
 *  const isSelected = getIsSelected(value);
 *  const onClick = React.useCallback(() => {
 *      if (!isDisabled) {
 *          toggleSelectedValue(value);
 *      }
 *  }, [toggleSelectedValue, value, isDisabled]);
 *
 *
 *  return (
 *      <RowItemCheckbox isSelected={isSelected} onClick={onClick}>
 *          <RowItemBody>Item</RowItemBody>
 *      </RowItemCheckbox>
 *  );
 * };
 * ```
 *
 * @returns ```javascript
 * {
 *  getIsSelected: (value) => void, // Used to determine whether the given value is the current selected value
 *  toggleSelectedValue: () => void, // Toggles the selected value
 * }
 * ```
 */
export const useSelectionMode = ({
  flattenedOptions,
  onChange,
  getIndexByValue,
  getSelectedOptionByValue,
  maxSelections,
  selectionMode,
  value,
}: SelectionModeProps) => {
  const getSelectedOption = React.useCallback(
    (val: string) => {
      const index = getIndexByValue?.(val);
      if (index !== undefined && flattenedOptions) {
        const realOption = flattenedOptions[index];
        if (realOption) {
          return realOption;
        }
      }
      // try to get a virtual option for the value as a fallback
      if (getSelectedOptionByValue) {
        return getSelectedOptionByValue(val);
      }
      return undefined;
    },
    [flattenedOptions, getIndexByValue]
  );

  const getSelectedOptions = React.useCallback(
    (values: string[]) => {
      return values.map(getSelectedOption).filter((item) => item !== undefined);
    },
    [getSelectedOption]
  );

  /**
   * An array of any selected options as Option objects.
   *
   * If no options are selected, this will be an empty array.
   * In selectionMode === 'single', this will be an array of length 1.
   */
  const selectedOptions = React.useMemo(() => {
    if (flattenedOptions && value) {
      if (selectionMode === 'multiple') {
        return getSelectedOptions(value);
      }

      const index = getIndexByValue?.(value as string);

      if (index !== undefined && flattenedOptions[index] !== undefined) {
        return [flattenedOptions[index]];
      }
    }
    return [];
  }, [
    getSelectedOptions,
    selectionMode,
    flattenedOptions,
    value,
    getIndexByValue,
  ]);

  const toggleSelectedValue = React.useCallback(
    (newValue: string, meta?: SelectionModeMeta) => {
      if (typeof onChange !== 'function') {
        return;
      }

      const option = getSelectedOption(newValue);

      switch (selectionMode) {
        case 'single': {
          // We are spreading meta here so it will always exist, even if nothing is provided.
          onChange(newValue, {option, ...meta});
          break;
        }

        case 'multiple': {
          let next = [...(value ?? [])];
          const canSelectMore = !maxSelections || next.length < maxSelections;

          if (next.includes(newValue)) {
            next = next.filter((elemValue) => elemValue !== newValue);
          } else if (canSelectMore) {
            next = next.concat(newValue);
          }

          const options = getSelectedOptions(next);

          onChange(next, {
            hasMaxSelections: checkHasMaxSelections({
              maxSelections,
              value: next,
            }),
            option,
            options,
            ...meta,
          });
          break;
        }
        default:
          break;
      }
    },
    [
      getSelectedOption,
      getSelectedOptions,
      onChange,
      maxSelections,
      selectionMode,
      value,
    ]
  );

  const getIsSelected = React.useCallback(
    (newValue: string) => {
      if (typeof value === 'string') {
        return value === newValue;
      } else if (Array.isArray(value)) {
        return value.includes(newValue);
      } else return false;
    },
    [value]
  );

  const hasMaxSelections = React.useMemo(
    () => checkHasMaxSelections({maxSelections, value}),
    [maxSelections, value]
  );

  return React.useMemo(
    () => ({
      toggleSelectedValue,
      getIsSelected,
      hasMaxSelections,
      selectedOptions,
    }),
    [toggleSelectedValue, getIsSelected, hasMaxSelections, selectedOptions]
  );
};
